import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ClipboardList,
  ExternalLink,
  FileText,
  Pencil,
  Plus,
  Sparkles,
} from 'lucide-react';

import { SubjectResourceLibrary } from '../SubjectResourceLibrary';
import { CreateMaterialChooser } from '../CreateMaterialChooser';
import { resourceKey } from '../../lib/studioResourceLinks';
import { CourseMaterialPreviewPane } from './CourseMaterialPreviewPane';
import { CoursePlanningPanel } from './CoursePlanningPanel';
import { StudioCreateModal } from '../StudioCreateModal';
import { PrintRubricDocument } from '../../print/PrintRubricDocument';
import { countResourcesForCourse } from '../../studioResources';
import type { StudioResource } from '../../studioResources';
import { getSubjectById } from '../../constants/subjectCatalog';
import {
  buildCourseRubricPath,
  buildPrintRubricPath,
} from '../../constants/courseStudioRoutes';
import { resolveCourseDisplayName } from '../../constants/courseProfileUi';
import type { CourseProfile } from '../../types/courseProfile';
import type { RubricDraft } from '../../types/rubricDraft';
import { deleteRubric } from '../../services/rubricStorage';
import type { StudioCreateResourceType } from '../../constants/studioCreateConstants';
import { useSalaAuth } from '../../../auth/SalaAuthContext';
import { resolveSalaTeacherDisplayName } from '../../../auth/resolveTeacherDisplayName';

export type CourseWorkspaceSection = 'materiales' | 'planificacion' | 'evaluacion';

type MaterialsMode = 'library' | 'create';

interface CourseWorkspaceProps {
  course: CourseProfile;
  section: CourseWorkspaceSection;
  rubrics: RubricDraft[];
  onRubricsChanged: () => void;
}

/**
 * Vista unificada del curso: lista izquierda + preview derecha.
 * Materiales / Planificación / Rúbricas solo cambian el contenido del mismo layout.
 * Las recetas interactivas viven en Taller interactivo (no aquí).
 */
export function CourseWorkspace({
  course,
  section,
  rubrics,
  onRubricsChanged,
}: CourseWorkspaceProps) {
  const { profile, user } = useSalaAuth();
  const teacherName = resolveSalaTeacherDisplayName(profile, user);
  const subject = getSubjectById(course.subjectId);
  const resourceCount = countResourcesForCourse(course.id);

  const [materialsMode, setMaterialsMode] = useState<MaterialsMode>(
    resourceCount > 0 ? 'library' : 'create',
  );
  const [selectedResource, setSelectedResource] = useState<StudioResource | null>(null);
  const [selectedRubricId, setSelectedRubricId] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);

  const [createAiOpen, setCreateAiOpen] = useState(false);
  const [defaultResourceType, setDefaultResourceType] = useState<
    StudioCreateResourceType | undefined
  >('guide');

  useEffect(() => {
    if (section === 'evaluacion') {
      setSelectedRubricId((current) => {
        if (current && rubrics.some((r) => r.id === current)) return current;
        return rubrics[0]?.id ?? null;
      });
    }
  }, [section, rubrics]);

  const selectedRubric = rubrics.find((r) => r.id === selectedRubricId) ?? null;

  const handleSelectResource = useCallback((resource: StudioResource) => {
    setSelectedResource(resource);
  }, []);

  const openCreate = (resourceType?: StudioCreateResourceType) => {
    setDefaultResourceType(resourceType ?? 'guide');
    setCreateAiOpen(true);
  };

  const handleDeleteRubric = (rubric: RubricDraft) => {
    if (!window.confirm(`¿Eliminar la rúbrica "${rubric.title || 'sin título'}"?`)) return;
    deleteRubric(rubric.id);
    onRubricsChanged();
    if (selectedRubricId === rubric.id) setSelectedRubricId(null);
  };

  if (section === 'planificacion') {
    return (
      <div className="course-workspace">
        <div className="course-workspace__toolbar">
          <div className="min-w-0">
            <p className="course-workspace__eyebrow">Planificación</p>
            <p className="course-workspace__hint">
              Unidades a la izquierda · detalle e hitos a la derecha
            </p>
          </div>
        </div>
        <CoursePlanningPanel
          course={course}
          layout="workspace"
          selectedUnitId={selectedUnitId}
          onSelectUnit={setSelectedUnitId}
        />
      </div>
    );
  }

  if (section === 'evaluacion') {
    return (
      <div className="course-workspace">
        <div className="course-workspace__toolbar">
          <div className="min-w-0">
            <p className="course-workspace__eyebrow">Rúbricas</p>
            <p className="course-workspace__hint">Selecciona una rúbrica para previsualizarla</p>
          </div>
          <Link to={buildCourseRubricPath(course.id, 'nueva')} className="course-workspace__cta">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Crear rúbrica
          </Link>
        </div>

        <section className="course-materials-workspace">
          <aside className="course-materials-workspace__list">
            {rubrics.length === 0 ? (
              <div className="sala-empty-state py-10">
                <ClipboardList
                  className="mx-auto h-8 w-8 text-[var(--micelio-terracota,#e07a5f)]"
                  strokeWidth={1.75}
                />
                <p className="sala-empty-state__title mt-3">Sin rúbricas todavía</p>
                <p className="sala-empty-state__hint">Crea la primera para este curso.</p>
              </div>
            ) : (
              <ul
                className="studio-resource-list studio-resource-list--picker space-y-2"
                role="listbox"
                aria-label="Rúbricas"
              >
                {rubrics.map((rubric) => {
                  const selected = rubric.id === selectedRubricId;
                  return (
                    <li key={rubric.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`studio-resource-row studio-soft-card studio-resource-row--selectable w-full text-left${
                          selected ? ' studio-resource-row--selected' : ''
                        }`}
                        onClick={() => setSelectedRubricId(rubric.id)}
                      >
                        <span className="studio-resource-row__icon" aria-hidden>
                          <ClipboardList className="h-4 w-4" />
                        </span>
                        <span className="studio-resource-row__body min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-[var(--sala-ink)]">
                            {rubric.title.trim() || 'Rúbrica sin título'}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-[var(--sala-ink-muted)]">
                            {rubric.hasMatrix
                              ? `${rubric.criteria.length} criterios · ${rubric.levelCount} niveles`
                              : 'Borrador sin matriz'}
                          </p>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>

          <div className="course-materials-workspace__preview">
            {selectedRubric ? (
              <div className="course-material-preview">
                <header className="course-material-preview__chrome">
                  <div className="min-w-0 flex-1">
                    <p className="course-material-preview__eyebrow">Vista rúbrica</p>
                    <h2 className="course-material-preview__title">
                      {selectedRubric.title.trim() || 'Rúbrica sin título'}
                    </h2>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedRubric.hasMatrix && selectedRubric.criteria.length > 0 ? (
                      <Link
                        to={buildPrintRubricPath(course.id, selectedRubric.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="course-material-preview__open"
                      >
                        Imprimir
                        <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden />
                      </Link>
                    ) : null}
                    <Link
                      to={buildCourseRubricPath(course.id, selectedRubric.id)}
                      className="course-material-preview__open course-material-preview__open--primary"
                    >
                      <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
                      Editar
                    </Link>
                    <button
                      type="button"
                      className="course-material-preview__open"
                      onClick={() => handleDeleteRubric(selectedRubric)}
                    >
                      Eliminar
                    </button>
                  </div>
                </header>
                <div className="course-material-preview__body">
                  <div className="course-material-preview__wysiwyg">
                    <PrintRubricDocument
                      rubric={selectedRubric}
                      courseDisplayName={resolveCourseDisplayName(course)}
                      subjectName={subject?.name}
                      schoolName={course.schoolName}
                      schoolLogoDataUrl={course.schoolLogoDataUrl}
                      teacherName={teacherName}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="course-material-preview course-material-preview--empty">
                <p className="course-material-preview__empty-title">Vista previa</p>
                <p className="course-material-preview__empty-hint">
                  Seleccioná una rúbrica o creá una nueva.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="course-workspace">
      <div className="course-workspace__toolbar">
        <div className="course-workspace__modes" role="tablist" aria-label="Modo de materiales">
          <button
            type="button"
            role="tab"
            aria-selected={materialsMode === 'library'}
            className={`course-workspace__mode${
              materialsMode === 'library' ? ' course-workspace__mode--active' : ''
            }`}
            onClick={() => setMaterialsMode('library')}
          >
            <FileText className="h-3.5 w-3.5" aria-hidden />
            Biblioteca
            {resourceCount > 0 ? (
              <span className="course-workspace__mode-count">{resourceCount}</span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={materialsMode === 'create'}
            className={`course-workspace__mode${
              materialsMode === 'create' ? ' course-workspace__mode--active' : ''
            }`}
            onClick={() => setMaterialsMode('create')}
          >
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Crear
          </button>
        </div>
      </div>

      {materialsMode === 'library' ? (
        <section className="course-materials-workspace">
          <aside className="course-materials-workspace__list">
            <div className="course-materials-sidebar">
              <header className="course-materials-sidebar__head">
                <div className="min-w-0 flex-1">
                  <p className="course-materials-sidebar__title">Biblioteca del curso</p>
                  <p className="course-materials-sidebar__meta">
                    {resourceCount === 0
                      ? 'Sin materiales aún'
                      : `${resourceCount} material${resourceCount === 1 ? '' : 'es'}`}
                  </p>
                </div>
                <button
                  type="button"
                  className="course-workspace__cta course-workspace__cta--compact"
                  onClick={() => openCreate('guide')}
                >
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                  Generar con IA
                </button>
              </header>
              <SubjectResourceLibrary
                subjectId={course.subjectId}
                subjectHue={subject?.hue ?? 'var(--sala-accent)'}
                courseId={course.id}
                selectionMode
                selectedKey={selectedResource ? resourceKey(selectedResource) : null}
                onSelectResource={handleSelectResource}
                onNewMaterial={() => openCreate('guide')}
                newMaterialLabel="Generar con IA"
              />
            </div>
          </aside>
          <div className="course-materials-workspace__preview">
            <CourseMaterialPreviewPane
              resource={selectedResource}
              subjectId={course.subjectId}
              courseId={course.id}
              subjectTag={course.subjectTag}
            />
          </div>
        </section>
      ) : (
        <CreateMaterialChooser
          subjectLabel={resolveCourseDisplayName(course)}
          onGenerate={openCreate}
          tallerHref={`/studio/herramientas?subject=${course.subjectId}`}
        />
      )}

      <StudioCreateModal
        open={createAiOpen}
        onOpenChange={setCreateAiOpen}
        course={course}
        subjectId={course.subjectId}
        subjectLabel={resolveCourseDisplayName(course)}
        defaultResourceType={defaultResourceType}
        initialStep="intencion"
      />
    </div>
  );
}
